import React, { useState, useRef, useCallback, useEffect } from 'react';
import { ArrowLeftRight, Sparkles } from 'lucide-react';

// Real before/after photo pairs from the identical perspective.
const samples = [
  {
    id: 'fassade',
    title: 'Fassadenreinigung',
    subtitle: 'Algen, Moos und Verschmutzungen schonend entfernt.',
    before: '/assets/ba-fassade-vorher.jpg',
    after: '/assets/ba-fassade-nachher.jpg',
  },
  {
    id: 'pflaster',
    title: 'Einfahrt & Pflaster',
    subtitle: 'Moos, Unkraut und Schmutz entfernt – Pflaster wieder hell.',
    before: '/assets/ba-pflaster-vorher.jpg',
    after: '/assets/ba-pflaster-nachher.jpg',
  },
  {
    id: 'dach',
    title: 'Dachreinigung',
    subtitle: 'Moos, Flechten und Ablagerungen restlos entfernt.',
    before: '/assets/ba-dach-vorher.jpg',
    after: '/assets/ba-dach-nachher.jpg',
  },
  {
    id: 'terrasse',
    title: 'Terrasse',
    subtitle: 'Natursteinplatten von Algenfilm und Moos befreit.',
    before: '/assets/ba-terrasse-vorher.jpg',
    after: '/assets/ba-terrasse-nachher.jpg',
  },
];

function CompareSlider({ before, after, title, subtitle }) {
  const [pos, setPos] = useState(52);
  const containerRef = useRef(null);
  const dragging = useRef(false);

  const setFromEvent = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    let p = ((clientX - rect.left) / rect.width) * 100;
    p = Math.max(0, Math.min(100, p));
    setPos(p);
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      if (!dragging.current) return;
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      setFromEvent(x);
    };
    const onUp = () => { dragging.current = false; document.body.style.cursor = ''; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchend', onUp);
    };
  }, [setFromEvent]);

  const onDown = (e) => {
    dragging.current = true;
    document.body.style.cursor = 'ew-resize';
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    setFromEvent(x);
  };

  return (
    <div className="bg-white rounded-2xl overflow-hidden card-shadow border border-slate-100">
      <div
        ref={containerRef}
        className="relative aspect-[4/3] w-full select-none touch-none"
        onMouseDown={onDown}
        onTouchStart={onDown}
      >
        {/* AFTER (clean) — full image */}
        <img src={after} alt={`${title} nachher`} className="absolute inset-0 w-full h-full object-cover" draggable={false} data-testid={`ba-after-${title}`} />

        {/* BEFORE (dirty) — real photo, revealed by clip on the LEFT of the handle */}
        <div
          className="absolute inset-0 overflow-hidden"
          style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
        >
          <img
            src={before}
            alt={`${title} vorher`}
            className="absolute inset-0 w-full h-full object-cover"
            draggable={false}
          />
        </div>

        {/* Labels */}
        <div className="pointer-events-none absolute top-4 left-4 bg-[#0B1E44]/85 text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded shadow-lg backdrop-blur-sm">
          Vorher
        </div>
        <div className="pointer-events-none absolute top-4 right-4 bg-[#E0A72C] text-[#0B1E44] text-[11px] font-extrabold uppercase tracking-wider px-3 py-1.5 rounded shadow-lg">
          Nachher
        </div>

        {/* Divider handle */}
        <div className="absolute inset-y-0 pointer-events-none" style={{ left: `${pos}%`, transform: 'translateX(-50%)' }}>
          <div className="w-[3px] h-full bg-white/95 shadow-[0_0_12px_rgba(255,255,255,0.6)]" />
          <div className="absolute top-1/2 -translate-y-1/2 -left-5 w-10 h-10 rounded-full bg-white shadow-[0_6px_16px_rgba(0,0,0,0.25)] flex items-center justify-center border-2 border-[#E0A72C]">
            <ArrowLeftRight className="w-4 h-4 text-[#0B1E44]" />
          </div>
        </div>
      </div>
      <div className="p-5 md:p-6">
        <h3 className="text-lg md:text-xl font-bold text-[#0B1E44]">{title}</h3>
        <p className="mt-1.5 text-slate-600 text-sm leading-relaxed">{subtitle}</p>
        <p className="mt-3 text-[11px] uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
          <Sparkles className="w-3 h-3 text-[#B8892A]" /> Regler bewegen · identische Perspektive
        </p>
      </div>
    </div>
  );
}

export default function BeforeAfter() {
  return (
    <section id="ergebnisse" className="py-20 md:py-28 bg-[#FAF7F2]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-3xl">
          <span className="crown-divider text-[#B8892A]">Ergebnisse</span>
          <h2 className="mt-4 text-3xl sm:text-4xl md:text-5xl font-extrabold text-[#0B1E44] leading-tight">
            Aus verwittert wird <span className="text-[#DC2626]">wieder gepflegt.</span>
          </h2>
          <p className="mt-5 text-slate-600 text-lg leading-relaxed">
            Verschieben Sie den Regler und sehen Sie den Unterschied: Alle Vorher/Nachher-Aufnahmen zeigen dieselbe Fläche aus identischer Perspektive – vor und nach der professionellen Aufbereitung.
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-2 gap-6 md:gap-8">
          {samples.map((s) => <CompareSlider key={s.id} {...s} />)}
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-3">
          <a href="#kontakt" className="btn-gold inline-flex items-center gap-2 px-6 py-3.5 rounded-md text-[15px]">Fläche prüfen lassen</a>
          <span className="text-sm text-slate-500">Das konkrete Ergebnis hängt von Material, Verschmutzung, Alter und Zustand der Fläche ab.</span>
        </div>
      </div>
    </section>
  );
}
